import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../axiosConfig';
import '../../styles/manageDeliverables/AddBook.css';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import BookFileField from './BookFileField';
import { bustCoverCacheForBook } from '../../components/PDFCoverPreview';

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

function authConfig(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

function fileUploadConfig(token, onUploadProgress) {
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': undefined,
    },
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    timeout: 0,
    onUploadProgress,
  };
}

export default function EditBookForm() {
  const navigate = useNavigate();
  const { bookId } = useParams();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    grade_id: '',
    subject_id: '',
    language_id: '',
    standard_id: '',
    country_id: '',
    booktype_id: '',
    version_label: '',
    isbn_code: '',
    url: '',
    digital_file: null,
    print_file: null,
    digital_cover_file: null,
    print_cover_file: null,
    zip_file: null,
  });
  const [digitalBookId, setDigitalBookId] = useState(null);
  const [printBookId, setPrintBookId] = useState(null);
  const [existingFiles, setExistingFiles] = useState({
    digital_file: false,
    digital_cover: false,
    print_file: false,
    print_cover: false,
    zip_file: false,
  });
  const [digitalFormatId, setDigitalFormatId] = useState('');
  const [printFormatId, setPrintFormatId] = useState('');
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [bookTypes, setBookTypes] = useState([]);
  const [standards, setStandards] = useState([]);
  const [countries, setCountries] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [selectedTags, setSelectedTags] = useState([]); // Array of {tag_id, tag_name}
  const [tagOptions, setTagOptions] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({
    version: 0,
    zip: 0,
    cover: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isbnError, setIsbnError] = useState('');
  const [versionError, setVersionError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [
          detailsRes,
          gradesRes,
          subjectsRes,
          languagesRes,
          bookTypesRes,
          standardsRes,
          countriesRes,
          formatsRes,
          tagsRes,
        ] = await Promise.all([
          axios.get(`/books/${bookId}/details`, { headers }),
          axios.get('/grades', { headers }),
          axios.get('/subjects', { headers }),
          axios.get('/languages', { headers }),
          axios.get('/booktypes', { headers }),
          axios.get('/standards', { headers }),
          axios.get('/countries', { headers }),
          axios.get('/book-formats', { headers }),
          axios.get('/tags', { headers }),
        ]);

        const bookData = detailsRes.data.book;
        if (!bookData) throw new Error('Book data not found');
        const pair = detailsRes.data.pair || {};
        const digitalPair = pair.digital || null;
        const printPair = pair.print || null;
        const formatName = String(bookData.format_name || '').toLowerCase();
        const latestHasFile = !!(
          detailsRes.data.latest_version?.has_file ||
          detailsRes.data.latest_version?.uploaded_link
        );
        const latestHasZip = !!(
          detailsRes.data.latest_version?.has_zip ||
          detailsRes.data.latest_version?.zip_link
        );
        const latestHasCover = !!(
          detailsRes.data.cover ||
          detailsRes.data.latest_version?.has_cover
        );
        setDigitalBookId(digitalPair?.book_id || (!formatName.includes('print') ? bookData.book_id : null));
        setPrintBookId(printPair?.book_id || (formatName.includes('print') ? bookData.book_id : null));
        setExistingFiles({
          digital_file: !!digitalPair?.has_file || (!formatName.includes('print') && latestHasFile),
          digital_cover: !!digitalPair?.has_cover || (!formatName.includes('print') && latestHasCover),
          print_file: !!printPair?.has_file || (formatName.includes('print') && latestHasFile),
          print_cover: !!printPair?.has_cover || (formatName.includes('print') && latestHasCover),
          zip_file: !!(digitalPair?.has_zip || printPair?.has_zip || latestHasZip),
        });
        setFormData({
          title: bookData.title || '',
          description: bookData.description || '',
          grade_id: bookData.grade_id || '',
          subject_id: bookData.subject_id || '',
          language_id: bookData.language_id || '',
          standard_id: bookData.standard_id || '',
          country_id: bookData.country_id || '',
          booktype_id: bookData.booktype_id || '',
          version_label: detailsRes.data.latest_version?.version_label || '',
          isbn_code: detailsRes.data.latest_version?.isbn_code || '',
          url: bookData.url || '',
          digital_file: null,
          print_file: null,
          digital_cover_file: null,
          print_cover_file: null,
          zip_file: null,
        });
        setGrades(gradesRes.data || []);
        setSubjects(subjectsRes.data || []);
        setLanguages(languagesRes.data || []);
        setBookTypes(bookTypesRes.data || []);
        setStandards(standardsRes.data || []);
        setCountries(countriesRes.data || []);
        const digitalFormat = (formatsRes.data || []).find(f => String(f.format_name).toLowerCase().includes('digital'));
        const printFormat = (formatsRes.data || []).find(f => String(f.format_name).toLowerCase().includes('print'));
        setDigitalFormatId(digitalFormat ? digitalFormat.format_id : '');
        setPrintFormatId(printFormat ? printFormat.format_id : '');
        setTagOptions((tagsRes.data || []).map(t => ({ tag_id: t.tag_id, tag_name: t.tag_name })));
        setSelectedTags(bookData.tags || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.error || 'Failed to fetch book details');
        setLoading(false);
      }
    };
    fetchData();
  }, [bookId]);

  // --- Validation and Formatting Functions (No changes needed) ---
  const formatISBN = (value) => {
    const digits = value.replace(/\D/g, '');
    return digits.replace(/(\d{3})(\d{1})(\d{6})(\d{2})(\d{1})?/, (_, a, b, c, d, e) => {
      if (!e) return `${a}-${b}-${c}-${d}`;
      return `${a}-${b}-${c}-${d}-${e}`;
    });
  };

  const validateISBN = (isbn) => {
    const digits = isbn.replace(/\D/g, '');
    if (digits.length === 0) {
      setIsbnError('');
      return true;
    }
    if (digits.length !== 13) {
      setIsbnError('ISBN must be 13 digits');
      return false;
    }
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += (i % 2 === 0 ? 1 : 3) * parseInt(digits[i]);
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    if (checkDigit !== parseInt(digits[12])) {
      setIsbnError('Invalid ISBN check digit');
      return false;
    }
    if (!digits.startsWith('978') && !digits.startsWith('979')) {
      setIsbnError('ISBN must start with 978 or 979');
      return false;
    }
    setIsbnError('');
    return true;
  };

  const formatVersion = (value) => {
    const cleaned = value.replace(/[^v0-9.]/gi, '');
    if (!cleaned.toLowerCase().startsWith('v')) return 'v' + cleaned;
    return cleaned;
  };

  const validateVersion = (version) => {
    if (!version) {
      setVersionError('Version label is required');
      return false;
    }
    if (!version.toLowerCase().startsWith('v')) {
      setVersionError('Version must start with "v"');
      return false;
    }
    const versionRegex = /^v\d+(\.\d+){0,2}$/;
    if (!versionRegex.test(version)) {
      setVersionError('Invalid version format. Use format: v1.0 or v1.0.0');
      return false;
    }
    const numbers = version.substring(1).split('.');
    for (const num of numbers) {
      if (parseInt(num) > 999) {
        setVersionError('Version numbers should not exceed 999');
        return false;
      }
    }
    setVersionError('');
    return true;
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else if (name === 'isbn_code') {
      const formattedISBN = formatISBN(value);
      setFormData(prev => ({ ...prev, [name]: formattedISBN }));
      validateISBN(formattedISBN);
    } else if (name === 'version_label') {
      const formattedVersion = formatVersion(value);
      setFormData(prev => ({ ...prev, [name]: formattedVersion }));
      validateVersion(formattedVersion);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      const value = tagInput.trim();
      if (!value) return;
      // Check if tag already selected
      if (selectedTags.some(t => t.tag_name.toLowerCase() === value.toLowerCase())) {
        setTagInput('');
        return;
      }
      // Check if tag exists
      const existing = tagOptions.find(t => t.tag_name.toLowerCase() === value.toLowerCase());
      if (existing) {
        setSelectedTags([...selectedTags, existing]);
      } else {
        // New tag (no id yet)
        setSelectedTags([...selectedTags, { tag_id: null, tag_name: value }]);
      }
      setTagInput('');
    }
  };

  const handleTagSelect = (tag) => {
    setSelectedTags((prev) => {
      const exists = prev.some(
        (t) =>
          (t.tag_id && tag.tag_id && t.tag_id === tag.tag_id) ||
          t.tag_name.toLowerCase() === tag.tag_name.toLowerCase()
      );
      if (exists) return prev;
      return [...prev, tag];
    });
    setTagInput('');
  };

  const handleRemoveTag = (tag) => {
    setSelectedTags(selectedTags.filter(t => t.tag_id !== tag.tag_id && t.tag_name !== tag.tag_name));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateISBN(formData.isbn_code) || !validateVersion(formData.version_label)) {
      return;
    }
    setIsSubmitting(true);
    setUploadProgress({ version: 0, zip: 0, cover: 0 });
    setUploadComplete(false);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const config = authConfig(token);

      const newTags = selectedTags.filter(t => !t.tag_id);
      const created = await Promise.all(newTags.map(async (t) => {
        const { data } = await axios.post('/tags', { tag_name: t.tag_name }, config);
        return { ...t, tag_id: data.tag_id };
      }));
      const resolvedTags = selectedTags.map((tag) => {
        if (tag.tag_id) return tag;
        return created.find((c) => c.tag_name === tag.tag_name) || tag;
      });
      const tag_ids = resolvedTags.map(t => t.tag_id).filter(Boolean);

      const bookPayload = (formatId) => ({
        title: formData.title,
        description: formData.description,
        grade_id: formData.grade_id,
        subject_id: formData.subject_id,
        language_id: formData.language_id,
        standard_id: formData.standard_id,
        country_id: formData.country_id,
        booktype_id: formData.booktype_id,
        isbn_code: formData.isbn_code,
        version_label: formData.version_label,
        format_id: formatId,
        tag_ids,
        url: formData.url,
      });

      const uploadVersionFile = async (book_id, file, attachZip) => {
        if (!file && !(attachZip && formData.zip_file)) return;

        const versionForm = new FormData();
        versionForm.append('book_id', book_id);
        versionForm.append('version_label', formData.version_label);
        versionForm.append('isbn_code', formData.isbn_code);
        if (file) versionForm.append('version_file', file);
        if (attachZip && formData.zip_file) versionForm.append('zip_file', formData.zip_file);

        const uploadCfg = fileUploadConfig(token, (e) => {
          if (!e.total) return;
          const percent = Math.round((e.loaded * 100) / e.total);
          setUploadProgress((prev) => ({
            ...prev,
            version: percent,
            zip: attachZip ? percent : prev.zip,
          }));
        });

        const { data: versionData } = await axios.get(`/books/${book_id}/versions`, config);
        const latestVersion = versionData.versions?.[0];

        if (latestVersion && file) {
          // Replace PDF on an existing version row
          await axios.put(`/books/book-versions/${latestVersion.version_id}`, versionForm, uploadCfg);
        } else if (latestVersion && !file && attachZip && formData.zip_file) {
          // ZIP-only update on existing version
          await axios.put(`/books/book-versions/${latestVersion.version_id}`, versionForm, uploadCfg);
        } else if (file) {
          // No version yet — create one (same path as Add Book)
          await axios.post('/books/book-versions', versionForm, uploadCfg);
        } else {
          throw new Error('A PDF version file is required to add this format.');
        }
      };

      const uploadCoverFile = async (book_id, coverFile, isNewBook) => {
        if (!coverFile) return;
        const coverForm = new FormData();
        coverForm.append('book_id', book_id);
        coverForm.append('file', coverFile);
        const coverCfg = fileUploadConfig(token, (e) => {
          if (!e.total) return;
          setUploadProgress((prev) => ({ ...prev, cover: Math.round((e.loaded * 100) / e.total) }));
        });
        if (isNewBook) {
          await axios.post('/books/covers', coverForm, coverCfg);
        } else {
          await axios.put(`/books/covers/${book_id}`, coverForm, coverCfg);
        }
      };

      const updateBookWithVersion = async (book_id, formatId, file, coverFile, attachZip) => {
        await axios.put(`/books/update/${book_id}`, bookPayload(formatId), config);
        await uploadVersionFile(book_id, file, attachZip);
        await uploadCoverFile(book_id, coverFile, false);
      };

      const createSiblingFormat = async (formatId, file, coverFile, attachZip, copyAccessFrom) => {
        if (!formatId) {
          throw new Error('Digital/Print format is not configured. Please add book formats first.');
        }
        if (!file) {
          throw new Error('Please choose a PDF file when adding a new Digital or Print version.');
        }
        const { data: book } = await axios.post(
          '/books',
          {
            ...bookPayload(formatId),
            copy_access_from: copyAccessFrom || undefined,
          },
          config
        );
        const book_id = book.book_id;
        await uploadVersionFile(book_id, file, attachZip);
        await uploadCoverFile(book_id, coverFile, true);
        return book_id;
      };

      // Prefer sequential updates so both Digital and Print PDFs replace reliably
      const siblingSourceId = digitalBookId || printBookId || Number(bookId);
      let nextDigitalId = digitalBookId;
      let nextPrintId = printBookId;

      if (nextDigitalId) {
        await updateBookWithVersion(
          nextDigitalId,
          digitalFormatId,
          formData.digital_file,
          formData.digital_cover_file,
          !!formData.zip_file
        );
      } else if (formData.digital_file || formData.digital_cover_file) {
        nextDigitalId = await createSiblingFormat(
          digitalFormatId,
          formData.digital_file,
          formData.digital_cover_file,
          !!formData.zip_file,
          siblingSourceId
        );
        setDigitalBookId(nextDigitalId);
      }

      if (nextPrintId) {
        await updateBookWithVersion(
          nextPrintId,
          printFormatId,
          formData.print_file,
          formData.print_cover_file,
          !!formData.zip_file && !nextDigitalId && !formData.digital_file
        );
      } else if (formData.print_file || formData.print_cover_file) {
        nextPrintId = await createSiblingFormat(
          printFormatId,
          formData.print_file,
          formData.print_cover_file,
          !!formData.zip_file && !nextDigitalId && !formData.digital_file,
          nextDigitalId || siblingSourceId
        );
        setPrintBookId(nextPrintId);
      }

      if (!nextDigitalId && !nextPrintId) {
        const fallbackId = Number(bookId);
        const fallbackFormat = digitalFormatId || printFormatId;
        if (fallbackId) {
          await axios.put(`/books/update/${fallbackId}`, bookPayload(fallbackFormat), config);
        }
      }

      // Drop stale browser cover previews for these books
      [nextDigitalId, nextPrintId, Number(bookId)].filter(Boolean).forEach((id) => {
        bustCoverCacheForBook(id);
      });

      setUploadComplete(true);
      setTimeout(() => {
        navigate('/admin/books');
      }, 800);
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError(err.response?.data?.error || err.message || 'Error updating book');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="books-table-loading">Loading book details...</div>;
  if (error && !formData.title) return <div className="error-message">{error}</div>;

  const availableTags = tagOptions
    .filter((t) => {
      const already = selectedTags.some(
        (st) =>
          (st.tag_id && t.tag_id && st.tag_id === t.tag_id) ||
          st.tag_name.toLowerCase() === t.tag_name.toLowerCase()
      );
      if (already) return false;
      const query = tagInput.trim().toLowerCase();
      if (!query) return true;
      return t.tag_name.toLowerCase().includes(query);
    })
    .sort((a, b) => a.tag_name.localeCompare(b.tag_name, undefined, { sensitivity: 'base' }));

  return (
    <form onSubmit={handleSubmit} className="enhanced-book-form">
      <div className="book-form-head">
        <button
          type="button"
          className="form-back-btn"
          onClick={() => navigate('/admin/books')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
          </svg>
          Back
        </button>
      </div>
      <h2>Edit Book</h2>
      <div className="form-grid">
        <label className="full-width">
          Book Title:
          <input type="text" name="title" value={formData.title} placeholder="Enter Book Title" onChange={handleChange} required/>
        </label>

        <label className="full-width">
          Description:
          <ReactQuill
            className="book-quill"
            value={formData.description}
            onChange={value => setFormData(prev => ({ ...prev, description: value }))}
            theme="snow"
            placeholder="Enter Description"
            modules={QUILL_MODULES}
          />
        </label>

        <label>
          Grade:
          <select name="grade_id" value={formData.grade_id} onChange={handleChange} required>
            <option value="">-- Select Grade --</option>
            {grades.map(g => <option key={g.grade_id} value={g.grade_id}>{g.grade_level}</option>)}
          </select>
        </label>

        <label>
          Subject:
          <select name="subject_id" value={formData.subject_id} onChange={handleChange} required>
            <option value="">-- Select Subject --</option>
            {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>)}
          </select>
        </label>

        <label>
          Language:
          <select name="language_id" value={formData.language_id} onChange={handleChange} required>
            <option value="">-- Select Language --</option>
            {languages.map(l => <option key={l.language_id} value={l.language_id}>{l.language_name}</option>)}
          </select>
        </label>

        <label>
          Standard:
          <select name="standard_id" value={formData.standard_id} onChange={handleChange} required>
            <option value="">-- Select Standard --</option>
            {standards.map(st => <option key={st.standard_id} value={st.standard_id}>{st.standard_name}</option>)}
          </select>
        </label>

        <label>
          Country:
          <select name="country_id" value={formData.country_id} onChange={handleChange} required>
            <option value="">-- Select Country --</option>
            {countries.map(c => <option key={c.country_id} value={c.country_id}>{c.country_name}</option>)}
          </select>
        </label>

        <label>
          Book Type:
          <select name="booktype_id" value={formData.booktype_id} onChange={handleChange} required>
            <option value="">-- Select Book Type --</option>
            {bookTypes.map(bt => <option key={bt.book_type_id} value={bt.book_type_id}>{bt.book_type_title}</option>)}
          </select>
        </label>

        <label>
          Version Label:
          <input type="text" name="version_label" value={formData.version_label} placeholder="e.g. v1.0 or v1.0.0" onChange={handleChange} required/>
          {versionError && <span className="error-message">{versionError}</span>}
        </label>

        <label>
          ISBN Code:
          <input type="text" name="isbn_code" value={formData.isbn_code} placeholder="Enter ISBN Code" onChange={handleChange} maxLength={17}/>
          {isbnError && <span className="error-message">{isbnError}</span>}
        </label>

        <label className="full-width">
          URL:
          <input
            type="url"
            name="url"
            placeholder="https://example.com"
            value={formData.url}
            onChange={handleChange}
          />
        </label>
      </div>

      <div className="book-form-section">
        <h3>Uploads</h3>
        <div className="file-upload-grid">
          <BookFileField
            label="Digital Version (PDF)"
            name="digital_file"
            accept="application/pdf"
            file={formData.digital_file}
            onChange={handleChange}
            existing={existingFiles.digital_file}
          />
          <BookFileField
            label="Digital Cover (PDF)"
            name="digital_cover_file"
            accept="application/pdf"
            file={formData.digital_cover_file}
            onChange={handleChange}
            existing={existingFiles.digital_cover}
          />
          <BookFileField
            label="Print Version (PDF)"
            name="print_file"
            accept="application/pdf"
            file={formData.print_file}
            onChange={handleChange}
            existing={existingFiles.print_file}
          />
          <BookFileField
            label="Print Cover (PDF)"
            name="print_cover_file"
            accept="application/pdf"
            file={formData.print_cover_file}
            onChange={handleChange}
            existing={existingFiles.print_cover}
          />
          <BookFileField
            label="ZIP File"
            name="zip_file"
            accept=".zip,application/zip,application/x-zip-compressed"
            file={formData.zip_file}
            onChange={handleChange}
            existing={existingFiles.zip_file}
            className="file-upload-zip"
          />
        </div>
      </div>

      <div className="form-grid">
        <div className="full-width">
          <span className="book-form-label">Tags</span>
          <div className="tag-input-container">
            <div className="tag-input-field-wrapper">
              <input
                type="text"
                placeholder="Search tags or type a new one"
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagInputKeyDown}
                className="tag-input-field"
              />
              <button
                type="button"
                className="tag-input-add-btn"
                onClick={() => handleTagInputKeyDown({ key: 'Enter', preventDefault: () => {} })}
              >
                + Add
              </button>
            </div>

            {selectedTags.length > 0 && (
              <div className="selected-tags-container">
                {selectedTags.map((tag) => (
                  <div key={tag.tag_id || tag.tag_name} className="selected-tag-pill">
                    <span>{tag.tag_name}</span>
                    <button
                      type="button"
                      className="remove-tag-btn"
                      onClick={() => handleRemoveTag(tag)}
                      aria-label={`Remove ${tag.tag_name}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <p className="tag-picker-heading">
              {tagInput.trim() ? 'Matching tags' : 'All tags'}
            </p>
            {availableTags.length > 0 ? (
              <div className="tag-picker-list">
                {availableTags.map((t) => (
                  <button
                    key={t.tag_id}
                    type="button"
                    className="tag-picker-item"
                    onClick={() => handleTagSelect(t)}
                  >
                    {t.tag_name}
                  </button>
                ))}
              </div>
            ) : (
              <p className="tag-picker-empty">
                {tagOptions.length === 0
                  ? 'No tags yet. Type a name and click Add.'
                  : tagInput.trim()
                    ? 'No matching tags. Click Add to create this tag.'
                    : 'All tags are already selected.'}
              </p>
            )}
          </div>
        </div>
      </div>

      {isSubmitting && (
        <div className="upload-progress-section">
          {(formData.digital_file || formData.print_file) && (
            <div>
              Version Upload: {uploadProgress.version}%
              <progress value={uploadProgress.version} max="100" />
            </div>
          )}
          {(formData.digital_cover_file || formData.print_cover_file) && (
            <div>
              Cover Upload: {uploadProgress.cover}%
              <progress value={uploadProgress.cover} max="100" />
            </div>
          )}
          {formData.zip_file && (
            <div>
              ZIP Upload: {uploadProgress.zip}%
              <progress value={uploadProgress.zip} max="100" />
            </div>
          )}
          {!formData.digital_file && !formData.print_file && !formData.zip_file && !formData.digital_cover_file && !formData.print_cover_file && (
            <div className="book-form-status">Saving book details…</div>
          )}
        </div>
      )}

      {uploadComplete && (
        <div className="success-message">
          Book updated successfully! Redirecting...
        </div>
      )}

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="form-actions">
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Updating...' : 'Update Book'}
        </button>
      </div>
    </form>
  );
}
